/**
 * useTenant Hook
 * React Query hook for fetching a single tenant by ID
 */

import { useQuery } from '@tanstack/react-query'

import { queryKeys } from '../../../lib/react-query/queryKeys'
import { fetchTenant } from '../api'

/**
 * Hook for fetching a single tenant by ID
 *
 * Features:
 * - Automatic caching (5min stale time, 10min gc time)
 * - Automatic refetching when tenant ID changes
 * - Error handling for non-existent tenants
 *
 * @param tenantId - Unique identifier of the tenant
 * @param options - Optional React Query options
 * @returns React Query result with tenant data
 *
 * @example
 * ```typescript
 * // Fetch tenant by ID
 * const { data: tenant, isLoading, error } = useTenant('123e4567-e89b-12d3-a456-426614174000')
 *
 * // Conditionally enable query
 * const { data } = useTenant(tenantId, { enabled: !!tenantId })
 *
 * // Access tenant data
 * if (tenant) {
 *   console.log(tenant.name)
 *   console.log(tenant.status)
 * }
 * ```
 */
export const useTenant = (
  tenantId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: queryKeys.tenants.detail(tenantId),
    queryFn: () => fetchTenant(tenantId),
    enabled: options?.enabled !== false && !!tenantId,
  })
}
